/**
 * `ApiError` — the single rejection type used by every module under
 * `frontend/src/api/`. Every non-2xx REST_API response and every
 * network-layer failure (DNS, timeout, socket) is converted to an
 * `ApiError` by the shared Axios response interceptor in `client.ts`,
 * so callers (TanStack Query hooks, route components) only ever have
 * to reason about one error shape.
 *
 * Requirements:
 * - Req 4.4: A rejected API_Client promise carries the HTTP status,
 *   the CMS error message, and the request URL.
 * - Req 17.1: Timeouts and network-layer failures surface with
 *   `status === 0` so the retry policy and the error UI can treat
 *   them uniformly.
 *
 * eslint note: only `src/api/client.ts` is allowed to import from
 * `axios` (Req 4.5). This module therefore duck-types Axios errors
 * from `unknown` instead of importing `AxiosError`.
 */

/**
 * Uniform rejection type for the Frontend API_Client (Req 4.4).
 *
 * `status === 0` is reserved for transport-level failures (timeouts,
 * DNS errors, socket errors) so that TanStack Query's retry policy
 * can distinguish them from HTTP responses (Req 17.1).
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly cmsMessage: string;
  public readonly requestUrl: string;
  public readonly code?: string;

  public constructor(
    status: number,
    cmsMessage: string,
    requestUrl: string,
    code?: string,
  ) {
    super(`[${status}] ${cmsMessage} (${requestUrl})`);
    this.name = 'ApiError';
    this.status = status;
    this.cmsMessage = cmsMessage;
    this.requestUrl = requestUrl;
    this.code = code;
  }

  /**
   * Convert an unknown error thrown by Axios into an `ApiError`.
   *
   * The shape of an `AxiosError` is duck-typed from `unknown` because
   * the ESLint rule in `.eslintrc.cjs` forbids every file except
   * `src/api/client.ts` from importing `axios` (Req 4.5).
   *
   * Mapping:
   * - Non-2xx HTTP response → `ApiError(response.status, wpMessage, url, wpCode)`
   *   where `wpMessage`/`wpCode` come from the WordPress error body
   *   `{ code, message, data: { status } }` when present.
   * - Timeout (`code === 'ECONNABORTED'` or message contains `timeout`)
   *   → `ApiError(0, 'Request timed out', url, 'ETIMEDOUT')` (Req 17.1).
   * - Network failure (`ERR_NETWORK`, `ENOTFOUND`, `ECONNREFUSED`, or
   *   no `response` at all) → `ApiError(0, 'Network error', url, 'ENETWORK')`
   *   (Req 17.1).
   * - Anything else → `ApiError(0, String(err.message ?? 'Unknown error'), url)`.
   */
  public static fromAxios(err: unknown, requestUrl: string): ApiError {
    // Guard: non-object / null / undefined values fall straight through
    // to the unknown-error branch so we never dereference them.
    if (err === null || err === undefined || typeof err !== 'object') {
      return new ApiError(0, 'Unknown error', requestUrl);
    }

    const errObj = err as Record<string, unknown>;
    const errCode = typeof errObj.code === 'string' ? errObj.code : undefined;
    const errMessage =
      typeof errObj.message === 'string' ? errObj.message : undefined;
    const response = isRecord(errObj.response) ? errObj.response : undefined;

    // 1. HTTP response present → treat as a non-2xx CMS response.
    if (response !== undefined) {
      const status =
        typeof response.status === 'number' ? response.status : 0;

      const data = response.data;
      let cmsMessage: string;
      let wpCode: string | undefined;

      if (isRecord(data)) {
        // WordPress standard error body: { code, message, data: { status } }
        if (typeof data.message === 'string' && data.message.length > 0) {
          cmsMessage = data.message;
        } else {
          cmsMessage = errMessage ?? safeStringify(data);
        }
        if (typeof data.code === 'string' && data.code.length > 0) {
          wpCode = data.code;
        }
      } else if (typeof data === 'string' && data.length > 0) {
        cmsMessage = data;
      } else {
        cmsMessage = errMessage ?? `HTTP ${status}`;
      }

      return new ApiError(status, cmsMessage, requestUrl, wpCode);
    }

    // 2. No response → transport-layer failure. `status = 0` per Req 17.1.
    const looksLikeTimeout =
      errCode === 'ECONNABORTED' ||
      errCode === 'ETIMEDOUT' ||
      (errMessage !== undefined && errMessage.toLowerCase().includes('timeout'));

    if (looksLikeTimeout) {
      return new ApiError(0, 'Request timed out', requestUrl, 'ETIMEDOUT');
    }

    const looksLikeNetwork =
      errCode === 'ERR_NETWORK' ||
      errCode === 'ENOTFOUND' ||
      errCode === 'ECONNREFUSED' ||
      errCode === 'ECONNRESET';

    if (looksLikeNetwork) {
      return new ApiError(0, 'Network error', requestUrl, 'ENETWORK');
    }

    // 3. Fallback: unknown shape, but no response — still `status = 0`
    //    so the retry policy treats it as a transport failure.
    return new ApiError(
      0,
      errMessage ?? 'Unknown error',
      requestUrl,
      errCode,
    );
  }
}

/**
 * Type guard for `ApiError`. Prefer this over ad-hoc `instanceof`
 * checks in callers so the discriminator is consistent (Req 4.4).
 */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/**
 * Narrow `unknown` to a plain object record without using `any`.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * `JSON.stringify` that never throws (circular refs → fallback string).
 */
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
